import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn';
import { Button, type ButtonVariant } from './Button';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: ButtonVariant;
}

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  /** Qué va a aparecer acá y cómo llega (máx. 2 líneas). */
  description?: ReactNode;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  size?: 'panel' | 'page';
  className?: string;
}

function ActionButton({ a, fallback }: { a: EmptyStateAction; fallback: ButtonVariant }) {
  if (a.href) {
    return (
      <Button asChild variant={a.variant ?? fallback}>
        <a href={a.href}>{a.label}</a>
      </Button>
    );
  }
  return (
    <Button variant={a.variant ?? fallback} onClick={a.onClick}>
      {a.label}
    </Button>
  );
}

/** Estado vacío (§6.23): nunca "No hay datos"; dice qué va a aparecer y cómo llega. */
export function EmptyState({ icon: Icon, title, description, action, secondaryAction, size = 'panel', className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        size === 'page' ? 'min-h-[50vh] py-16' : 'py-10',
        className,
      )}
    >
      {Icon ? <Icon size={32} strokeWidth={1.5} className="mb-3 text-faint" aria-hidden /> : null}
      <h3 className="m-0 text-h3 text-ink">{title}</h3>
      {description ? <p className="m-0 mt-1.5 max-w-[46ch] text-body text-muted">{description}</p> : null}
      {action || secondaryAction ? (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action ? <ActionButton a={action} fallback="secondary" /> : null}
          {secondaryAction ? <ActionButton a={secondaryAction} fallback="ghost" /> : null}
        </div>
      ) : null}
    </div>
  );
}
